import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchAdminEmailPreview,
  fetchAdminEmailStatus,
  postAdminBulkInvitationEmails,
  postAdminSendTestEmail,
  putAdminInvitationSettings,
} from "../../api/adminEmail";
import AdminAppLayout from "../../components/admin/AdminAppLayout";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { useFormSubmitGuard } from "../../hooks/useFormSubmitGuard";

function firstNameFromDisplay(displayName) {
  const s = String(displayName || "").trim();
  if (!s) return "";
  return s.split(/\s+/)[0] || s;
}

export default function AdminEmailTestPage() {
  const { user } = useAdminAuth();
  const [to, setTo] = useState("");
  const [sesConfigured, setSesConfigured] = useState(null);
  const [autoSendOnInvite, setAutoSendOnInvite] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [pending, setPending] = useState(false);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewSubject, setPreviewSubject] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  useFormSubmitGuard(pending || settingsBusy || bulkBusy);

  const loadStatus = useCallback(async () => {
    const data = await fetchAdminEmailStatus();
    setSesConfigured(Boolean(data?.sesConfigured));
    if (data?.invitationSettings && typeof data.invitationSettings.invitationAutoSendOnInvite === "boolean") {
      setAutoSendOnInvite(data.invitationSettings.invitationAutoSendOnInvite);
    }
    setSettingsLoaded(true);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void loadStatus().then(() => {
      if (!cancelled) {
        /* noop */
      }
    });
    return () => {
      cancelled = true;
    };
  }, [loadStatus]);

  useEffect(() => {
    const email = String(user?.email || "").trim();
    if (email) setTo((prev) => (prev.trim() ? prev : email));
  }, [user?.email]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setPending(true);
    try {
      const out = await postAdminSendTestEmail(to);
      const greet = out.greetingPreview?.recipientName ? ` Greeting: “Dear ${out.greetingPreview.recipientName},”.` : "";
      setSuccess(
        out.dev
          ? "Dev mode: no email sent (configure live API + SES to test)."
          : `Sent. Message id: ${out.messageId || "—"}${greet}`
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Send failed.");
    } finally {
      setPending(false);
    }
  }

  async function handleLoadPreview() {
    setPreviewError(null);
    setPreviewBusy(true);
    try {
      const out = await fetchAdminEmailPreview();
      setPreviewSubject(out.subject || null);
      setPreviewHtml(out.htmlBody || null);
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : "Could not load preview.");
    } finally {
      setPreviewBusy(false);
    }
  }

  async function handleSaveAutomation() {
    setError(null);
    setSuccess(null);
    setSettingsBusy(true);
    try {
      await putAdminInvitationSettings(autoSendOnInvite);
      setSuccess(
        autoSendOnInvite
          ? "Saved: when you mark someone invited, we will try to send the invitation email automatically (SES must work)."
          : "Saved: auto-send after “Mark invited” is off. Use bulk send or send per person from People."
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed.");
    } finally {
      setSettingsBusy(false);
    }
  }

  async function handleBulkSend(force) {
    const msg = force
      ? "Resend the invitation email to every invited guest, even if we already sent one?"
      : "Send the invitation email to every invited guest who has not been sent one yet?";
    if (!window.confirm(msg)) return;
    setError(null);
    setSuccess(null);
    setBulkBusy(true);
    try {
      const out = await postAdminBulkInvitationEmails({ force });
      const s = out.summary;
      setSuccess(
        `Bulk finished: sent ${s.sent}, skipped ${s.skipped}, failed ${s.failed} (processed ${s.attempted} invited rows).`
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Bulk send failed.");
    } finally {
      setBulkBusy(false);
    }
  }

  const greetingHint = firstNameFromDisplay(user?.displayName) || "friend";

  return (
    <AdminAppLayout>
      <header className="admin-app-pagehead admin-app-pagehead--split">
        <div>
          <p className="admin-dashboard-status">Admin</p>
          <h1 className="admin-app-title">Test email</h1>
          <p className="admin-guests-lead admin-guests-lead--tight">
            The preview send uses your <strong>admin profile name</strong> for the greeting (Dear {greetingHint},) but
            delivers to the address you enter below (useful in the SES sandbox). Live sends from People use each guest’s
            own name and email.
          </p>
        </div>
        <Link to="/admin" className="admin-app-back">
          Dashboard
        </Link>
      </header>

      <section className="admin-panel admin-panel--form">
        {sesConfigured === false ? (
          <p className="admin-login-error" role="status">
            SES is not configured on the server (<code className="admin-code-inline">SES_FROM_ADDRESS</code>). Deploy
            the API with a verified From address.
          </p>
        ) : null}
        {error ? (
          <p className="admin-login-error" role="alert">
            {error}
          </p>
        ) : null}
        {success ? (
          <p className="admin-email-test-success" role="status">
            {success}
          </p>
        ) : null}
        <form className="admin-login-form" onSubmit={handleSubmit} aria-busy={pending} data-submitting={pending ? "true" : undefined}>
          <fieldset className="admin-form-fieldset" disabled={pending || sesConfigured === false}>
            <legend className="visually-hidden">Recipient</legend>
            <label htmlFor="admin-email-test-to">Send test to (email)</label>
            <input
              id="admin-email-test-to"
              name="to"
              type="email"
              autoComplete="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              required
              placeholder="you@example.com"
            />
            <button type="submit">{pending ? "Sending…" : "Send test email"}</button>
          </fieldset>
          {pending ? (
            <p className="form-status-text" role="status" aria-live="polite">
              Sending — please wait.
            </p>
          ) : null}
        </form>
      </section>

      <section className="admin-panel" style={{ marginTop: "1.5rem" }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
          <h2 className="admin-app-title" style={{ fontSize: "1.15rem", marginBottom: "0.5rem" }}>
            Preview
          </h2>
          <button type="button" disabled={previewBusy} onClick={() => void handleLoadPreview()}>
            {previewBusy ? "Loading…" : previewHtml ? "Refresh preview" : "Load preview"}
          </button>
        </div>
        <p className="admin-guests-lead admin-guests-lead--tight">
          Renders the invitation exactly as it will send — no email is sent, nothing leaves the server.
        </p>
        {previewError ? (
          <p className="admin-login-error" role="alert">
            {previewError}
          </p>
        ) : null}
        {previewHtml ? (
          <div style={{ marginTop: "1rem" }}>
            {previewSubject ? (
              <p className="admin-guests-muted" style={{ marginBottom: "0.5rem" }}>
                Subject: <strong>{previewSubject}</strong>
              </p>
            ) : null}
            <iframe
              title="Invitation email preview"
              srcDoc={previewHtml}
              style={{ width: "100%", height: "80vh", border: "1px solid #c7cec9", borderRadius: "0.5rem", background: "#fff" }}
            />
          </div>
        ) : null}
      </section>

      <section className="admin-panel admin-panel--form" style={{ marginTop: "1.5rem" }}>
        <h2 className="admin-app-title" style={{ fontSize: "1.15rem", marginBottom: "0.5rem" }}>
          Invitation rollout
        </h2>
        <p className="admin-guests-lead admin-guests-lead--tight">
          Turn on auto-send to fire the invitation email when you click <strong>Mark invited</strong> on People.
          Anyone marked invited later will get the email automatically while this stays on. Use{" "}
          <strong>Reset invitation</strong> on a row to clear send flags so bulk or auto can run again; use{" "}
          <strong>Send invite</strong> / resend from People for one-off sends.
        </p>
        {settingsLoaded ? (
          <div className="admin-form-fieldset" style={{ border: "none", padding: 0 }}>
            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={autoSendOnInvite}
                onChange={(e) => setAutoSendOnInvite(e.target.checked)}
                disabled={settingsBusy || bulkBusy || sesConfigured === false}
              />
              <span>Automatically send invitation email when marking a guest as invited</span>
            </label>
            <div style={{ marginTop: "0.75rem", display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
              <button type="button" disabled={settingsBusy || sesConfigured === false} onClick={() => void handleSaveAutomation()}>
                {settingsBusy ? "Saving…" : "Save automation"}
              </button>
              <button
                type="button"
                disabled={bulkBusy || sesConfigured === false}
                onClick={() => void handleBulkSend(false)}
              >
                {bulkBusy ? "Working…" : "Send to invited (not sent yet)"}
              </button>
              <button
                type="button"
                disabled={bulkBusy || sesConfigured === false}
                onClick={() => void handleBulkSend(true)}
              >
                Resend all invited
              </button>
            </div>
          </div>
        ) : (
          <p className="admin-guests-muted">Loading settings…</p>
        )}
      </section>
    </AdminAppLayout>
  );
}
